import { useState, type FormEvent } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { usePhoneField, useEmailField } from "../lib/useValidatedField";
import { api } from "../lib/api";

const LEDGER = [
  { job: "Maintenance wash + interior wipe", pays: "$140", cut: "$42" },
  { job: "Interior deep clean / extraction", pays: "$300", cut: "$90" },
  { job: "Full detail — interior and exterior", pays: "$450", cut: "$135" },
  { job: "1-step correction + sealant", pays: "$750", cut: "$225" },
  { job: "New-car prep + ceramic coating", pays: "$1,200", cut: "$360" },
  { job: "2-step correction + ceramic coating", pays: "$1,800", cut: "$540", total: true },
];

const EXPECTATIONS = [
  { n: "01", title: "One vehicle a day", body: "No stacked bookings, no rushing a coating to make a second appointment. The schedule is built to let you finish properly." },
  { n: "02", title: "Paid weekly, every Friday", body: "Direct deposit for the prior week's completed jobs. A statement shows each job, the invoice, and your share." },
  { n: "03", title: "Set your own availability", body: "Tell us the days you want. Full-time and two-days-a-week technicians are both on the roster." },
  { n: "04", title: "Paid training on coatings", body: "Manufacturer certification covered by the house, including the installer credential you keep for your own career." },
  { n: "05", title: "Mileage and fuel covered", body: "The van, its fuel, and its upkeep are the house's expense. Your own car stays in the driveway." },
  { n: "06", title: "No sales quota", body: "Technicians are not asked to upsell on a driveway. The price was set before you arrived, and it stands." },
];

const STEPS = [
  { n: "I", title: "Application", body: "The form below. Photographs of your own work matter more than a résumé." },
  { n: "II", title: "Conversation", body: "Twenty minutes on the phone about your process, your paint experience, and your availability." },
  { n: "III", title: "Paid trial day", body: "One real appointment, alongside the house. Paid at the same 30% whether or not you continue." },
  { n: "IV", title: "On the roster", body: "Contract, certification scheduled, and your first week of appointments assigned." },
];

export default function Detailers() {
  const phone = usePhoneField();
  const email = useEmailField();
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [experience, setExperience] = useState("1–3 years");
  const [availability, setAvailability] = useState("Full-time, 5 days");
  const [polish, setPolish] = useState("2step");
  const [workLink, setWorkLink] = useState("");
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const required = { name, phone: phone.value, email: email.value, city };
    const miss = new Set<string>();
    for (const [k, v] of Object.entries(required)) if (!v.trim()) miss.add(k);
    setMissing(miss);
    if (miss.size) return;
    if (!email.validate()) return;
    if (!phone.validate()) return;

    setSubmitError("");
    try {
      await api.post("/applications", {
        name,
        phone: phone.value,
        email: email.value,
        city,
        experience,
        availability,
        polishExperience: polish,
        workLink,
      });
      setSent(true);
    } catch {
      setSubmitError("Couldn't send that — call (410) 555-0118 and the house will take it down directly.");
    }
  };

  return (
    <>
      <SiteHeader variant="solid" active="detailers" />
      <main>
        <section className="masthead" data-screen-label="Detailers masthead">
          <div className="wrap">
            <p className="eyebrow">Detailers · Now hiring</p>
            <h1>Keep 30% of every job. Bring nothing but the craft.</h1>
            <p className="lede">
              Foundry books the work, screens the client, sets the price, and supplies the van, the water, the power,
              and the coatings. You arrive at a confirmed appointment with a firm scope and a finished quote, and you
              detail. No marketing, no chasing, no invoicing.
            </p>
            <div className="mast-acts">
              <a href="#apply" className="btn">
                Apply to detail
              </a>
              <a href="#math" className="btn btn--ghost">
                See the numbers
              </a>
            </div>
            <div className="mast-meta">
              <span>30% commission on every completed job</span>
              <span>Paid weekly · 1099 contractor</span>
              <span>Ellicott City and 20 miles</span>
            </div>
          </div>
        </section>

        <section data-screen-label="Positioning">
          <div className="wrap split">
            <div>
              <p className="eyebrow">Why here</p>
              <h2>The hard part of this trade was never the polishing.</h2>
            </div>
            <div>
              <p className="lede">
                Most detailers spend half their week on everything that isn't detailing — ads that don't convert,
                tyre-kickers who want $80 for a full correction, no-shows, receipts, chasing payment. The work itself
                is the easy part. The business around it is what burns people out.
              </p>
              <p className="lede">
                Foundry runs that half. One vehicle per technician per day, a client who already agreed the scope and
                the price, and a van that arrives stocked. You do the part you're good at, at a standard you can be
                proud of, and you get paid on Friday.
              </p>
            </div>
          </div>
        </section>

        <section id="math" className="math" data-screen-label="Commission">
          <div className="wrap">
            <p className="eyebrow">Commission</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "22ch" }}>30% of the invoice, not of what's left over</h2>
            <p className="lede">
              Commission is calculated on the full job price before any cost of goods. Products, pads, coatings,
              water, fuel, and insurance come out of the house's share, not yours.
            </p>
            <div className="math-grid">
              <div className="stat">
                <span className="mono">Typical day</span>
                <div className="big">$225–360</div>
                <p>A single correction or full-detail appointment, one vehicle, one technician.</p>
              </div>
              <div className="stat stat--signed">
                <span className="mono" style={{ color: "var(--wg-brass)" }}>
                  Full week
                </span>
                <div className="big">$1,100–1,800</div>
                <p>Five appointments, weighted toward correction and coating work.</p>
              </div>
              <div className="stat">
                <span className="mono">Your outlay</span>
                <div className="big">$0</div>
                <p>No product cost, no equipment purchase, no lead spend, no booking software.</p>
              </div>
            </div>
            <div className="ledger">
              <table>
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Client pays</th>
                    <th className="amt">Your 30%</th>
                  </tr>
                </thead>
                <tbody>
                  {LEDGER.map((r) => (
                    <tr key={r.job} className={r.total ? "total" : undefined}>
                      <td>{r.job}</td>
                      <td className="amt">{r.pays}</td>
                      <td className="amt">{r.cut}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="caption">
              Figures are per completed appointment, before tax. Contractors are paid every Friday for the week
              prior. Multi-day restoration work is split across the days worked.
            </p>
          </div>
        </section>

        <section className="handle" data-screen-label="Division of work">
          <div className="wrap">
            <p className="eyebrow">Division of work</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "20ch" }}>We handle the hard stuff.</h2>
            <p className="lede">Nothing below is a promise to sort out later. It is how the house already runs.</p>
            <div className="cols">
              <div>
                <h3>Foundry handles</h3>
                <ul>
                  <li>
                    <strong>Finding the client</strong>Advertising, the site, the reviews, the phone. Leads arrive
                    already warm.
                  </li>
                  <li>
                    <strong>Qualifying and quoting</strong>Paint condition assessed and the price agreed before anyone
                    drives anywhere.
                  </li>
                  <li>
                    <strong>Scheduling</strong>Routed appointments inside a 20-mile radius, confirmed the day before.
                  </li>
                  <li>
                    <strong>The van and the kit</strong>Water tank, generator, polishers, lighting, pads, chemicals,
                    coatings. Replenished for you.
                  </li>
                  <li>
                    <strong>Money</strong>Deposits, payment, invoicing, chargebacks, collections. You never ask a
                    client for a card.
                  </li>
                  <li>
                    <strong>Insurance and warranty</strong>General liability and garage-keepers cover. Coating
                    registration and any warranty claim.
                  </li>
                  <li>
                    <strong>Difficult conversations</strong>Scope disputes, expectations, redos. The house takes those,
                    not the technician.
                  </li>
                </ul>
              </div>
              <div>
                <h3>You handle</h3>
                <ul className="cols--yours">
                  <li>
                    <strong>The finish</strong>Prep, correction, coating, interior. The work, done to the house
                    standard.
                  </li>
                  <li>
                    <strong>The vehicle in your care</strong>Careful, methodical, unhurried. One car a day exists so
                    this is possible.
                  </li>
                  <li>
                    <strong>The record</strong>Photographs before and after, and a short note of what was done for the
                    client's file.
                  </li>
                  <li>
                    <strong>Showing up</strong>On time, presentable, and straight with the client about what the paint
                    will and won't do.
                  </li>
                </ul>
              </div>
            </div>
            <p className="note">
              Tooling stays with the house, which also means it stays maintained. If a polisher fails mid-job, it is
              replaced — not deducted.
            </p>
          </div>
        </section>

        <section data-screen-label="What you get">
          <div className="wrap">
            <p className="eyebrow">Terms</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "24ch" }}>What working here actually looks like</h2>
            <div className="exp-grid">
              {EXPECTATIONS.map((x) => (
                <div className="exp" key={x.n}>
                  <span className="mono">{x.n}</span>
                  <div>
                    <h3>{x.title}</h3>
                    <p>{x.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="path" data-screen-label="Hiring path">
          <div className="wrap">
            <p className="eyebrow">How hiring works</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "22ch" }}>Four steps, about two weeks</h2>
            <div className="steps">
              {STEPS.map((s) => (
                <div className="step" key={s.n}>
                  <span className="mono" style={{ color: "var(--wg-brass)" }}>
                    {s.n}
                  </span>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section data-screen-label="Detailer voices">
          <div className="wrap">
            <p className="eyebrow">From the roster</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "22ch" }}>Technicians on the arrangement</h2>
            <div className="quotes">
              <blockquote>
                <p>I ran my own thing for four years. I made more money here in my third month, and I stopped working Sundays on invoices.</p>
                <footer>Detailer · Two years on the roster</footer>
              </blockquote>
              <blockquote>
                <p>Nobody asks me to hurry a coating. That alone is worth the split.</p>
                <footer>Detailer · Coating specialist</footer>
              </blockquote>
            </div>
          </div>
        </section>

        <section id="apply" className="apply" data-screen-label="Application">
          <div className="wrap">
            <p className="eyebrow">Apply</p>
            <h2 style={{ margin: "var(--space-6) 0 var(--space-5)", maxWidth: "20ch" }}>Apply to detail</h2>
            <p className="lede">Seven fields. Every application gets a reply within two working days.</p>
            <div className="form-grid">
              <div className="aside">
                <dl>
                  <div>
                    <dt>Who we're looking for</dt>
                    <dd>Technicians with real machine-polishing hours, a clean driving record, and the patience to do one car properly.</dd>
                  </div>
                  <div>
                    <dt>Not required</dt>
                    <dd>Your own van, your own products, certification, or a client list.</dd>
                  </div>
                  <div>
                    <dt>Questions first</dt>
                    <dd>
                      <a href="tel:+14105550118">(410) 555-0118</a>
                    </dd>
                  </div>
                  <div>
                    <dt>Or email</dt>
                    <dd>
                      <a href="mailto:crew@foundrydetailing.com">crew@foundrydetailing.com</a>
                    </dd>
                  </div>
                </dl>
              </div>
              <div>
                {!sent ? (
                  <form id="applyForm" noValidate onSubmit={onSubmit}>
                    <div className="fields">
                      <div>
                        <label htmlFor="a-name">Name</label>
                        <input
                          id="a-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="First and last"
                          style={missing.has("name") ? { borderBottomColor: "#C99A92" } : undefined}
                        />
                      </div>
                      <div>
                        <label htmlFor="a-phone">Telephone</label>
                        <input
                          id="a-phone"
                          type="tel"
                          autoComplete="tel"
                          value={phone.value}
                          onChange={(e) => phone.onChange(e.target.value)}
                          onBlur={phone.onBlur}
                          placeholder="(000) 000-0000"
                          style={missing.has("phone") || phone.invalid ? { borderBottomColor: "#C99A92" } : undefined}
                        />
                        <div className={`msg${phone.invalid ? " bad" : ""}`}>{phone.message}</div>
                      </div>
                      <div>
                        <label htmlFor="a-email">Email</label>
                        <input
                          id="a-email"
                          type="email"
                          autoComplete="email"
                          value={email.value}
                          onChange={(e) => email.onChange(e.target.value)}
                          onBlur={email.onBlur}
                          placeholder="you@address.com"
                          style={missing.has("email") || email.invalid ? { borderBottomColor: "#C99A92" } : undefined}
                        />
                        <div className={`msg${email.invalid ? " bad" : ""}`}>{email.message}</div>
                      </div>
                      <div>
                        <label htmlFor="a-city">City you'd work from</label>
                        <input
                          id="a-city"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="Catonsville"
                          style={missing.has("city") ? { borderBottomColor: "#C99A92" } : undefined}
                        />
                      </div>
                      <div>
                        <label htmlFor="a-exp">Paid detailing experience</label>
                        <select id="a-exp" value={experience} onChange={(e) => setExperience(e.target.value)}>
                          <option>Under 1 year</option>
                          <option>1–3 years</option>
                          <option>3–6 years</option>
                          <option>6 years or more</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="a-avail">Availability</label>
                        <select id="a-avail" value={availability} onChange={(e) => setAvailability(e.target.value)}>
                          <option>Full-time, 5 days</option>
                          <option>3–4 days a week</option>
                          <option>2 days a week</option>
                          <option>Weekends only</option>
                        </select>
                      </div>
                      <div className="span2">
                        <label>Machine polishing experience</label>
                        <div className="radios">
                          <label>
                            <input type="radio" name="polish" checked={polish === "2step"} onChange={() => setPolish("2step")} />{" "}
                            Two-step correction, confident
                          </label>
                          <label>
                            <input type="radio" name="polish" checked={polish === "1step"} onChange={() => setPolish("1step")} /> One-step,
                            confident
                          </label>
                          <label>
                            <input type="radio" name="polish" checked={polish === "learning"} onChange={() => setPolish("learning")} /> Still
                            learning
                          </label>
                        </div>
                      </div>
                      <div className="span2">
                        <label htmlFor="a-work">Your work — a link, or tell us about a car you're proud of</label>
                        <textarea
                          id="a-work"
                          value={workLink}
                          onChange={(e) => setWorkLink(e.target.value)}
                          placeholder="Instagram, Google Drive, or a paragraph. Coatings you've installed, paint you've saved."
                        />
                      </div>
                    </div>
                    <div className="form-foot">
                      <button type="submit" className="btn">
                        Submit application
                      </button>
                      <small>Contractor position · 1099 · Paid trial day either way.</small>
                    </div>
                    {submitError && <p className="msg bad">{submitError}</p>}
                  </form>
                ) : (
                  <div className="sent">
                    <h3>Received.</h3>
                    <p>
                      The house will reply within two working days. If you'd rather talk first, call{" "}
                      <a href="tel:+14105550118">(410) 555-0118</a>.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter variant="detailers" />
    </>
  );
}
